import jsPDF from "jspdf";
import { toast } from "sonner";

const ROLE = { public_entity: "Ente Pubblico", business: "Impresa / P.IVA", admin: "Portale", artist: "Artista", supplier: "Fornitore" };
const money = (n) => `EUR ${Number(n || 0).toFixed(2)}`;
const dt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT") : "-");

export const CLAUSES = [
  "1. Oggetto. L'Artista/Fornitore si impegna a eseguire la prestazione indicata nella data e nel luogo sopra riportati; il Committente si impegna a corrispondere il compenso pattuito.",
  "2. Compenso. Il compenso comprende cachet, trasferta (0,40 EUR/km A/R) ed eventuale opzione, oltre IVA di legge. Per le Pubbliche Amministrazioni è prevista la fatturazione elettronica tramite SDI.",
  "3. Obblighi del Committente. Il Committente garantisce l'accesso al luogo dell'evento, gli spazi e le autorizzazioni necessarie allo svolgimento della prestazione.",
  "4. Obblighi dell'Artista/Fornitore. L'Artista/Fornitore si impegna a presentarsi puntuale e a svolgere la prestazione con professionalità e secondo quanto concordato.",
  "5. Annullamento e forza maggiore. In caso di impedimento per cause non imputabili alle parti (maltempo grave, provvedimenti delle autorità, malattia) le parti concordano in buona fede una nuova data o la risoluzione senza penali.",
  "6. Perfezionamento. Il contratto è firmato dal Committente e si perfeziona con la controfirma dell'Artista/Fornitore entro 24 ore dalla ricezione; in mancanza la proposta decade e la data torna disponibile.",
  "7. Privacy. I dati delle parti sono trattati ai sensi del Reg. UE 2016/679 (GDPR) esclusivamente per l'esecuzione del presente contratto.",
];

const partyLines = (p = {}) => [
  p.name,
  ROLE[p.role] || "",
  p.referentName && `Referente: ${p.referentName}`,
  (p.firstName || p.lastName) && !p.referentName && p.role !== "public_entity" && `Rappresentante: ${[p.firstName, p.lastName].filter(Boolean).join(" ")}`,
  p.vatNumber && `P.IVA: ${p.vatNumber}`,
  p.taxCode && `C.F.: ${p.taxCode}`,
  p.ipaCode && `Codice IPA: ${p.ipaCode}`,
  p.sdiCode && `Codice SDI: ${p.sdiCode}`,
  (p.billingAddress || p.city || p.baseLocation) && `Sede: ${p.billingAddress || p.city || p.baseLocation}`,
  p.pec && `PEC: ${p.pec}`,
  p.email && `Email: ${p.email}`,
].filter(Boolean);

export function contractPdf(b) {
  try { buildPdf(b); }
  catch { toast.error("Impossibile generare il PDF del contratto"); }
}

function buildPdf(b) {
  const doc = new jsPDF();
  const L = 20, W = 170;
  let y = 20;
  const ensure = (h) => { if (y + h > 280) { doc.addPage(); y = 20; } };
  const para = (text, size = 10, style = "normal") => {
    doc.setFont("helvetica", style); doc.setFontSize(size);
    doc.splitTextToSize(text, W).forEach((ln) => { ensure(6); doc.text(ln, L, y); y += size * 0.5; });
  };
  const section = (title) => { y += 4; ensure(12); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(2, 46, 93); doc.text(title, L, y); doc.setDrawColor(213, 163, 76); doc.line(L, y + 1.5, L + W, y + 1.5); doc.setTextColor(0); y += 8; };

  doc.setFillColor(2, 46, 93); doc.rect(0, 0, 210, 14, "F");
  doc.setTextColor(213, 163, 76); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text("ARTISTI IN COMUNE - Portale Istituzionale", L, 9);
  doc.setTextColor(0); y = 28;
  doc.setFontSize(16); doc.text("CONTRATTO DI PRESTAZIONE ARTISTICA / DI SERVIZIO", 105, y, { align: "center" }); y += 6;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(`Rif. ${b.id}  -  Generato il ${dt(b.createdAt)}`, 105, y, { align: "center" }); y += 6;

  section("PARTI");
  const cl = partyLines(b.clientInfo || { name: b.clientName });
  const ar = partyLines(b.artistInfo || { name: b.artistName });
  doc.setFontSize(9); doc.setFont("helvetica", "bold");
  doc.text("COMMITTENTE", L, y); doc.text("ARTISTA / FORNITORE", L + 88, y); y += 5;
  doc.setFont("helvetica", "normal");
  for (let i = 0; i < Math.max(cl.length, ar.length); i++) {
    if (cl[i]) doc.text(doc.splitTextToSize(cl[i], 82)[0], L, y);
    if (ar[i]) doc.text(doc.splitTextToSize(ar[i], 82)[0], L + 88, y);
    y += 4.6;
  }

  section("EVENTO");
  [["Data evento", b.eventDate], ["Orario", b.eventTime || "da concordare"], ["Luogo", b.eventLocation], ["Tipo evento", b.eventType],
   ["Trasferta A/R", `${b.distanceKm || 0} km`],
   ["Opzione", b.optionType === "paid_72h" ? "Opzione 72h" : b.optionType === "free_24h" ? "Opzione gratuita 24h" : "Prenotazione diretta"],
   ...(b.cig ? [["CIG", b.cig]] : []), ...(b.cup ? [["CUP", b.cup]] : [])]
    .forEach(([k, v]) => { doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.text(`${k}:`, L, y); doc.setFont("helvetica", "normal"); doc.text(String(v || "-"), L + 40, y); y += 6; });
  if (b.cpv?.length) { doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.text("CPV:", L, y); y -= 4; doc.setFont("helvetica", "normal"); b.cpv.forEach((c) => { y += 4; doc.text(doc.splitTextToSize(`${c.code} - ${c.label}`, W - 40)[0], L + 40, y); }); y += 6; }

  section("CONDIZIONI ECONOMICHE");
  if (b.totalGross !== undefined) {
    para(`Imponibile netto: ${money(b.subtotalNet)}    ${b.vatLabel || "IVA 22%"}: ${money(b.vat)}`);
    para(`Totale: ${money(b.totalGross)}`, 11, "bold");
    if (b.splitPayment) {
      y += 1;
      para("Scissione dei pagamenti (Split Payment, art. 17-ter D.P.R. 633/1972):", 9, "bold");
      para(`Importo da corrispondere all'operatore (imponibile): ${money(b.payableToOperator)}`, 9);
      para(`IVA versata direttamente dall'Ente all'Erario: ${money(b.vatToErario)}`, 9);
    }
  }

  section("CLAUSOLE");
  CLAUSES.forEach((c) => { para(c, 9); y += 1.5; });

  if (b.notes) { section("CONDIZIONI PARTICOLARI"); para(b.notes, 9); }

  section("FIRME");
  ensure(50);
  const sig = (x, label, img, name, at, pending) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.text(label, x, y);
    if (img) doc.addImage(img, "PNG", x, y + 2, 70, 26);
    else { doc.setFont("helvetica", "italic"); doc.text(pending, x, y + 16); }
    doc.setDrawColor(150); doc.line(x, y + 30, x + 75, y + 30);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    if (name) doc.text(`${name} - firmato il ${dt(at)}`, x, y + 35);
  };
  const pending = b.status === "pending_artist" ? "In attesa di controfirma" : b.status === "confirmed" ? "-" : "Non controfirmato";
  sig(L, "Il Committente", b.clientSignature, b.clientSignerName, b.clientSignedAt, "-");
  sig(L + 90, "L'Artista / Fornitore", b.artistSignature, b.artistSignerName, b.artistSignedAt, pending);
  y += 44;
  doc.setFontSize(7.5); doc.setTextColor(110);
  para("Firme apposte elettronicamente tramite il portale Artisti in Comune. Documento generato automaticamente.", 7.5);

  doc.save(`Contratto-${(b.artistName || "artista").replace(/\s+/g, "-")}-${b.eventDate}.pdf`);
}
